import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm, useFieldArray, Controller } from 'react-hook-form'
import DatePicker, { registerLocale } from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import '../../styles/datepicker.css'
import fr from 'date-fns/locale/fr'
import { addDays } from 'date-fns'

registerLocale('fr', fr)
import api from '../../services/api'
import axios from 'axios'
import { useAuthStore } from '../../stores/authStore'
import {
    ArrowLeft,
    ArrowRight,
    Check,
    Search,
    Plus,
    Trash2,
    Loader2,
    User,
    Users,
    FileText,
    Calendar,
    MapPin,
    AlertCircle
} from 'lucide-react'
import toast from 'react-hot-toast'
import './RendezvousAddPage.css' // Reuse styles
import AddressAutocomplete from '../../components/AddressAutocomplete'

const steps = [
    { key: 'client', icon: User },
    { key: 'famille', icon: Users },
    { key: 'resume', icon: FileText }
]

export default function RendezvousEditPage() {
    const { id } = useParams()
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { user, isSuperAdmin } = useAuthStore()

    useEffect(() => {
        if (user?.profil?.code_profil === 'AGENT') {
            toast.error(t('common.unauthorized'))
            navigate('/dashboard')
        }
    }, [user, navigate, t])

    const [currentStep, setCurrentStep] = useState(0)
    const [loading, setLoading] = useState(true) // Initial loading true
    const [submitting, setSubmitting] = useState(false)
    const [searchPhone, setSearchPhone] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const [searching, setSearching] = useState(false)
    const [searchPerformed, setSearchPerformed] = useState(false)
    const [selectedClient, setSelectedClient] = useState(null)
    const [addressLoading, setAddressLoading] = useState(false)
    const [referenceData, setReferenceData] = useState({
        lamal: [],
        lca: [],
        etats_sante: [],
        config: {}
    })
    const [rdv, setRdv] = useState(null)
    const [ageType, setAgeType] = useState('dob')

    const applyPhoneMask = (value, mask) => {
        if (!value) return value
        // Simple formatter: keeps digits and spaces, tries to adhere to chunks
        // Better implementation would use the specific mask characters (09 etc)
        // For '0XX XXX XX XX' or '099 999 99 99', we can just insert spaces after specific counts
        // Removing all non-digits first
        let digits = value.replace(/\D/g, '')

        // If mask is standard Swiss-ish: 000 000 00 00
        if (digits.length > 10) digits = digits.slice(0, 10) // Limit to 10

        let res = ''
        if (digits.length > 0) res += digits.substring(0, 3)
        if (digits.length > 3) res += ' ' + digits.substring(3, 6)
        if (digits.length > 6) res += ' ' + digits.substring(6, 8)
        if (digits.length > 8) res += ' ' + digits.substring(8, 10)

        return res
    }

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        trigger,
        control,
        formState: { errors, isValid },
        reset
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            rdvDate: '',
            rdvTime: '',
            commentaireAgent_rdv: '',
            client: {
                nom_client: '',
                prenom_client: '',
                natel_client: '',
                fix_client: '',
                natel2_client: '',
                fix2_client: '',
                date_naissance: '',
                age: '',
                adresse_client: {
                    adresse: '',
                    etage: '',
                    npa: '',
                    ville: '',
                    indications: ''
                }
            },
            famille: []
        }
    })

    const { fields: familleFields, append, remove } = useFieldArray({
        control,
        name: 'famille'
    })

    useEffect(() => {
        const init = async () => {
            await fetchReferenceData()
            await fetchRendezvous()
        }
        init()
    }, [id])

    const fetchReferenceData = async () => {
        try {
            const [lamalRes, lcaRes, santéRes, configRes] = await Promise.all([
                api.get('/reference/lamal'),
                api.get('/reference/lca'),
                api.get('/reference/etats-sante'),
                api.get('/reference/config')
            ])

            setReferenceData({
                lamal: lamalRes.data.data.options,
                lca: lcaRes.data.data.options,
                etats_sante: santéRes.data.data.options,
                config: configRes.data.data.config
            })
        } catch (error) {
            console.error('Error fetching reference data:', error)
        }
    }

    const fetchRendezvous = async () => {
        try {
            const response = await api.get(`/rendezvous/${id}`)
            const rdvData = response.data.data.rendezvous
            setRdv(rdvData)
            setSelectedClient(rdvData.client_rdv)

            // Parse date
            const rdvDate = rdvData.dateHeure_rdv // Keep as full ISO for DatePicker

            reset({
                rdvDate,
                // rdvTime is no longer needed for logic but kept if referenced elsewhere, though we should clean up
                commentaireAgent_rdv: rdvData.commentaireAgent_rdv || '',
                client: {
                    nom_client: rdvData.client_rdv.nom_client,
                    prenom_client: rdvData.client_rdv.prenom_client,
                    natel_client: rdvData.client_rdv.natel_client,
                    fix_client: rdvData.client_rdv.fix_client || '',
                    natel2_client: rdvData.client_rdv.natel2_client || '',
                    fix2_client: rdvData.client_rdv.fix2_client || '',
                    date_naissance: rdvData.client_rdv.date_naissance || '',
                    age: rdvData.client_rdv.age || '',
                    adresse_client: {
                        adresse: rdvData.client_rdv.adresse_client?.adresse || '',
                        etage: rdvData.client_rdv.adresse_client?.etage || '',
                        npa: rdvData.client_rdv.adresse_client?.npa || '',
                        ville: rdvData.client_rdv.adresse_client?.ville || '',
                        indications: rdvData.client_rdv.adresse_client?.indications || ''
                    }
                },
                famille: rdvData.client_rdv.famille || []
            })
        } catch (error) {
            toast.error(t('common.error'))
            navigate('/rendezvous')
        } finally {
            setLoading(false)
        }
    }

    // Permission check for date editing
    const canEditDate = () => {
        if (!rdv) return false
        if (isSuperAdmin()) return true

        const profileCode = user?.profil?.code_profil
        if (profileCode === 'SUPERVISEUR' || profileCode === 'QUALITE') return true

        // Assigned Conseiller
        if (rdv.conseiller_rdv && (rdv.conseiller_rdv._id === user._id || rdv.conseiller_rdv === user._id)) return true

        return false
    }

    const lookupAddress = async (address) => {
        if (!address || address.length < 5) return

        setAddressLoading(true)
        try {
            const response = await axios.get('https://nominatim.openstreetmap.org/search', {
                params: {
                    q: address,
                    format: 'json',
                    addressdetails: 1,
                    countrycodes: 'ch',
                    limit: 1
                }
            })

            if (response.data && response.data.length > 0) {
                const data = response.data[0]
                const addressDetails = data.address

                const postcode = addressDetails.postcode
                const city = addressDetails.city || addressDetails.town || addressDetails.village || addressDetails.municipality

                if (postcode) setValue('client.adresse_client.npa', postcode, { shouldValidate: true })
                if (city) setValue('client.adresse_client.ville', city, { shouldValidate: true })
            }
        } catch (error) {
            console.error('Address lookup failed', error)
        } finally {
            setAddressLoading(false)
        }
    }

    const handleSearchClient = async () => {
        if (searchPhone.length < 3) return

        setSearching(true)
        setSearchPerformed(true)
        try {
            const response = await api.get('/clients/search', { params: { phone: searchPhone } })
            setSearchResults(response.data.data.clients)
            if (response.data.data.clients.length === 0) {
                toast(t('rendezvous.noClientFound'), { icon: '🔍' })
            }
        } catch (error) {
            toast.error(t('common.error'))
            setSearchResults([])
        } finally {
            setSearching(false)
        }
    }

    const handleSelectClient = (client) => {
        setSelectedClient(client)
        setValue('client.nom_client', client.nom_client)
        setValue('client.prenom_client', client.prenom_client)
        setValue('client.natel_client', client.natel_client)
        setValue('client.fix_client', client.fix_client || '')
        setValue('client.natel2_client', client.natel2_client || '')
        setValue('client.fix2_client', client.fix2_client || '')
        setValue('client.adresse_client.adresse', client.adresse_client?.adresse || '')
        setValue('client.adresse_client.etage', client.adresse_client?.etage || '')
        setValue('client.adresse_client.npa', client.adresse_client?.npa || '')
        setValue('client.adresse_client.ville', client.adresse_client?.ville || '')
        setValue('client.adresse_client.indications', client.adresse_client?.indications || '')

        setValue('famille', []) // Clear first
        if (client.famille && client.famille.length > 0) {
            client.famille.forEach((member) => append(member))
        }

        setSearchResults([])
        setSearchPhone('')
        setSearchPerformed(false)
        toast.success(t('rendezvous.clientSelected'))
    }

    const addFamilyMember = () => {
        append({
            typeFamille: 'Adulte',
            prenomFamille: '',
            ageType: 'dob',
            age: '',
            datenaissanceFamille: '',
            etatSanteFamille: 'Excellente',
            franchiseFamille: '',
            lamalFamille: '',
            lcaFamille: '',
            depuisFamille: ''
        })
    }

    const validateStep1 = async () => {
        const fieldsToValidate = [
            'rdvDate',
            'rdvTime',
            'client.nom_client',
            'client.prenom_client',
            'client.natel_client',
            'client.adresse_client.adresse',
            'client.adresse_client.npa',
            'client.adresse_client.ville'
        ]
        const isValid = await trigger(fieldsToValidate)
        if (!isValid) toast.error(t('validation.fixErrors'))
        return isValid
    }

    const nextStep = async (e) => {
        if (e) e.preventDefault()
        if (currentStep === 0) {
            const isValid = await validateStep1()
            if (!isValid) return
        }
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1)
        }
    }

    const previousStep = () => {
        if (currentStep > 0) setCurrentStep(currentStep - 1)
    }

    const onSubmit = async (data) => {
        setSubmitting(true)
        try {
            // rdvDate is now full ISO string from DatePicker
            const dateHeure_rdv = data.rdvDate

            const payload = {
                dateHeure_rdv,
                commentaireAgent_rdv: data.commentaireAgent_rdv,
                client: {
                    ...data.client,
                    famille: data.famille?.map(m => ({
                        ...m,
                        age: (m.ageType === 'dob' || !m.ageType) ? null : m.age,
                        datenaissanceFamille: m.ageType === 'age' ? null : m.datenaissanceFamille
                    }))
                }
            }

            await api.put(`/rendezvous/${id}`, payload)
            toast.success(t('common.success'))
            navigate('/rendezvous')
        } catch (error) {
            const data = error.response?.data
            const msg = data?.message
            if (msg === 'APPOINTMENT_DATE_MUST_BE_FUTURE') {
                toast.error(t('validation.APPOINTMENT_DATE_MUST_BE_FUTURE'))
            } else if (msg === 'APPOINTMENT_INTERVAL_INVALID') {
                toast.error(t('validation.APPOINTMENT_INTERVAL_INVALID'))
            } else if (msg === 'APPOINTMENT_TIME_OUT_OF_BOUNDS') {
                toast.error(t('validation.APPOINTMENT_TIME_OUT_OF_BOUNDS'))
            } else if (msg === 'You are not authorized to change the appointment date') {
                toast.error(t('rendezvous.dateEditDenied'))
            } else if (msg === 'VALIDATION_FAILED' && data?.errors?.length > 0) {
                const firstError = data.errors[0]
                toast.error(t(`validation.${firstError.message}`, { defaultValue: t('validation.fixErrors') }))
            } else {
                toast.error(msg || t('common.error'))
            }
        } finally {
            setSubmitting(false)
        }
    }

    const formData = watch()
    const familyValues = watch('famille')

    // Config for TimePicker
    const config = referenceData.config || {}
    const getHoursMinutes = (str) => {
        if (!str) return { h: 8, m: 0 }
        const parts = str.split(':')
        return { h: parseInt(parts[0]), m: parseInt(parts[1]) || 0 }
    }

    const startConfig = getHoursMinutes(config.heureDebut || '08:00')
    const endConfig = getHoursMinutes(config.heureFin || '18:00')
    const interval = parseInt(config.intervale || '15')

    const isDateEditable = canEditDate()
    const allowPast = referenceData.config.allowPastAppointments === true

    const renderStep = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className="wizard-step">
                        <h3>{t('rendezvous.wizard.step1')}</h3>

                        {/* Date & Time (Read Only) & Age/DOB Selection */}
                        <div className="form-row">
                            <div className="form-group" style={{ flex: 1 }}>
                                <label className="form-label">
                                    <Calendar size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                                    {t('rendezvous.dateLabel')} & {t('rendezvous.timeLabel')}
                                </label>
                                {isDateEditable ? (
                                    <Controller
                                        control={control}
                                        name="rdvDate"
                                        rules={{ required: t('validation.required') }}
                                        render={({ field }) => {
                                            const dateBase = field.value ? new Date(field.value) : new Date();
                                            return (
                                                <DatePicker
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => field.onChange(date ? date.toISOString() : '')}
                                                    showTimeSelect
                                                    timeFormat="HH:mm"
                                                    timeIntervals={interval}
                                                    dateFormat="dd.MM.yyyy HH:mm"
                                                    timeCaption="Heure"
                                                    timeClassName={(time) => {
                                                        const currentMinutes = time.getHours() * 60 + time.getMinutes()
                                                        const startMinutes = startConfig.h * 60 + startConfig.m
                                                        const endMinutes = endConfig.h * 60 + endConfig.m

                                                        if (currentMinutes < startMinutes || currentMinutes > endMinutes) {
                                                            return "hidden-time-item"
                                                        }
                                                        return ""
                                                    }}
                                                    className={`form-input ${errors.rdvDate ? 'error' : ''}`}
                                                    placeholderText="dd.mm.yyyy HH:mm"
                                                    locale={i18n.language.startsWith('fr') ? 'fr' : undefined}
                                                    autoComplete='off'
                                                    minDate={allowPast ? null : addDays(new Date(), 1)}
                                                />
                                            );
                                        }}
                                    />
                                ) : (
                                    <div className="text-lg font-medium mt-1">
                                        {formData.rdvDate ? new Date(formData.rdvDate).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                                    </div>
                                )}
                                {errors.rdvDate && <span className="form-error">{errors.rdvDate.message}</span>}
                            </div>

                            {/* Replaced Time with Client Age/DOB Selection */}
                            <div className="form-group" style={{ flex: 1 }}>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <div style={{ flex: 1 }}>
                                        <label className="form-label">Type</label>
                                        <select
                                            className="form-select"
                                            value={ageType}
                                            onChange={(e) => setAgeType(e.target.value)}
                                            disabled={user?.profil?.code_profil === 'AGENT'}
                                        >
                                            <option value="dob">Date de naissance</option>
                                            <option value="age">Age</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label className="form-label">
                                            {ageType === 'dob' ? 'Date de naissance' : 'Age'}
                                        </label>
                                        {ageType === 'dob' ? (
                                            <>
                                                <Controller
                                                    control={control}
                                                    name="client.date_naissance"
                                                    rules={{ required: ageType === 'dob' ? t('validation.required') : false }}
                                                    render={({ field }) => (
                                                        <DatePicker
                                                            selected={field.value ? new Date(field.value) : null}
                                                            onChange={(date) => field.onChange(date ? date.toISOString() : '')}
                                                            dateFormat="dd.MM.yyyy"
                                                            showYearDropdown
                                                            scrollableYearDropdown
                                                            yearDropdownItemNumber={100}
                                                            className="form-input"
                                                            placeholderText="dd.mm.yyyy"
                                                            locale={i18n.language.startsWith('fr') ? 'fr' : undefined}
                                                            autoComplete='off'
                                                            maxDate={new Date()}
                                                            disabled={user?.profil?.code_profil === 'AGENT'}
                                                        />
                                                    )}
                                                />
                                                {errors.client?.date_naissance && (
                                                    <span className="form-error">{errors.client.date_naissance.message}</span>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <input
                                                    type="number"
                                                    className="form-input"
                                                    placeholder="Age"
                                                    {...register('client.age', { required: ageType === 'age' ? t('validation.required') : false })}
                                                    disabled={user?.profil?.code_profil === 'AGENT'}
                                                />
                                                {errors.client?.age && (
                                                    <span className="form-error">{errors.client.age.message}</span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">{t('client.lastName')} *</label>
                                <input
                                    type="text"
                                    className={`form-input ${errors.client?.nom_client ? 'error' : ''}`}
                                    {...register('client.nom_client', { required: t('validation.required') })}
                                    disabled={user?.profil?.code_profil === 'AGENT'}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('client.firstName')} *</label>
                                <input
                                    type="text"
                                    className={`form-input ${errors.client?.prenom_client ? 'error' : ''}`}
                                    {...register('client.prenom_client', { required: t('validation.required') })}
                                    disabled={user?.profil?.code_profil === 'AGENT'}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">{t('client.mobile')} *</label>
                                <Controller
                                    control={control}
                                    name="client.natel_client"
                                    rules={{ required: t('validation.required') }}
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="tel"
                                            className={`form-input ${errors.client?.natel_client ? 'error' : ''}`}
                                            onChange={(e) => {
                                                const masked = applyPhoneMask(e.target.value, referenceData.config?.phoneMask)
                                                field.onChange(masked)
                                            }}
                                            disabled={user?.profil?.code_profil === 'AGENT'}
                                            placeholder={referenceData.config?.phoneMask ? referenceData.config.phoneMask.replace(/9/g, '_') : '0__ ___ __ __'}
                                        />
                                    )}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('client.phone')}</label>
                                <Controller
                                    control={control}
                                    name="client.fix_client"
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="tel"
                                            className="form-input"
                                            onChange={(e) => {
                                                const masked = applyPhoneMask(e.target.value, referenceData.config?.phoneMask)
                                                field.onChange(masked)
                                            }}
                                            disabled={user?.profil?.code_profil === 'AGENT'}
                                            placeholder={referenceData.config?.phoneMask ? referenceData.config.phoneMask.replace(/9/g, '_') : '0__ ___ __ __'}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">{t('client.mobile2')}</label>
                                <Controller
                                    control={control}
                                    name="client.natel2_client"
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="tel"
                                            className="form-input"
                                            onChange={(e) => {
                                                const masked = applyPhoneMask(e.target.value, referenceData.config?.phoneMask)
                                                field.onChange(masked)
                                            }}
                                            disabled={user?.profil?.code_profil === 'AGENT'}
                                            placeholder={referenceData.config?.phoneMask ? referenceData.config.phoneMask.replace(/9/g, '_') : '0__ ___ __ __'}
                                        />
                                    )}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('client.phone2')}</label>
                                <Controller
                                    control={control}
                                    name="client.fix2_client"
                                    render={({ field }) => (
                                        <input
                                            {...field}
                                            type="tel"
                                            className="form-input"
                                            onChange={(e) => {
                                                const masked = applyPhoneMask(e.target.value, referenceData.config?.phoneMask)
                                                field.onChange(masked)
                                            }}
                                            disabled={user?.profil?.code_profil === 'AGENT'}
                                            placeholder={referenceData.config?.phoneMask ? referenceData.config.phoneMask.replace(/9/g, '_') : '0__ ___ __ __'}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="address-section-block">
                            <h4 className="section-title"><MapPin size={16} /> {t('client.address')}</h4>
                            <div className="form-row">
                                <div className="form-group" style={{ flex: 2 }}>
                                    <label className="form-label">{t('client.address')} *</label>
                                    <Controller
                                        control={control}
                                        name="client.adresse_client.adresse"
                                        rules={{ required: t('validation.required') }}
                                        render={({ field }) => (
                                            <AddressAutocomplete
                                                value={field.value}
                                                onChange={field.onChange}
                                                onSelect={(data) => {
                                                    field.onChange(data.adresse)
                                                    setValue('client.adresse_client.npa', data.npa, { shouldValidate: true })
                                                    setValue('client.adresse_client.ville', data.ville, { shouldValidate: true })
                                                }}
                                                error={!!errors.client?.adresse_client?.adresse}
                                                placeholder={t('client.address')}
                                                disabled={user?.profil?.code_profil === 'AGENT'}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('client.floor')}</label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        {...register('client.adresse_client.etage')}
                                        disabled={user?.profil?.code_profil === 'AGENT'}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">{t('client.npa')} *</label>
                                    <input
                                        type="text"
                                        className={`form-input ${errors.client?.adresse_client?.npa ? 'error' : ''}`}
                                        {...register('client.adresse_client.npa', { required: t('validation.required') })}
                                        disabled={user?.profil?.code_profil === 'AGENT'}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">{t('client.city')} *</label>
                                    <input
                                        type="text"
                                        className={`form-input ${errors.client?.adresse_client?.ville ? 'error' : ''}`}
                                        {...register('client.adresse_client.ville', { required: t('validation.required') })}
                                        disabled={user?.profil?.code_profil === 'AGENT'}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('client.indications')}</label>
                                <textarea
                                    className="form-textarea"
                                    rows={3}
                                    {...register('client.adresse_client.indications')}
                                    disabled={user?.profil?.code_profil === 'AGENT'}
                                />
                            </div>
                        </div>
                    </div>
                )

            case 1:
                return (
                    <div className="wizard-step">
                        <div className="step-header">
                            <h3>{t('family.title')}</h3>
                            <button type="button" className="btn btn-secondary" onClick={addFamilyMember}>
                                <Plus size={18} /> {t('family.add')}
                            </button>
                        </div>
                        {familleFields.length === 0 ? (
                            <div className="empty-state">
                                <Users size={48} className="text-muted" />
                                <p className="text-muted">{t('family.noMembers')}</p>
                                <button type="button" className="btn btn-primary" onClick={addFamilyMember}>
                                    <Plus size={18} /> {t('family.add')}
                                </button>
                            </div>
                        ) : (
                            <div className="family-list">
                                {familleFields.map((field, index) => {
                                    // Use watched value if available to control age type
                                    const memberValues = familyValues ? familyValues[index] : {}
                                    const memberAgeType = memberValues?.ageType || (memberValues?.age ? 'age' : 'dob')

                                    return (
                                        <div key={field.id} className="family-card">
                                            <div className="family-card-header">
                                                <span>{t('family.title')} #{index + 1}</span>
                                                <button type="button" className="btn btn-icon btn-ghost" onClick={() => remove(index)}>
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                            <div className="family-card-body">
                                                {/* Line 1: Type Famille */}
                                                <div className="form-row">
                                                    <div className="form-group" style={{ width: 'auto', minWidth: '150px', flex: '0 0 auto' }}>
                                                        <label className="form-label">{t('family.type')}</label>
                                                        <select
                                                            className="form-select"
                                                            {...register(`famille.${index}.typeFamille`)}
                                                        >
                                                            <option value="Adulte">{t('family.adult')}</option>
                                                            <option value="Enfant">{t('family.child')}</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Line 2: Prénom + Age/DOB */}
                                                <div className="form-row">
                                                    <div className="form-group" style={{ flex: 1 }}>
                                                        <label className="form-label">{t('family.firstName')}</label>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            {...register(`famille.${index}.prenomFamille`)}
                                                        />
                                                    </div>

                                                    <div className="form-group" style={{ flex: 1 }}>
                                                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
                                                            <div style={{ minWidth: '100px' }}>
                                                                <label className="form-label" style={{ display: 'block' }}>Type</label>
                                                                <select
                                                                    className="form-select"
                                                                    {...register(`famille.${index}.ageType`)}
                                                                >
                                                                    <option value="dob">Date</option>
                                                                    <option value="age">Age</option>
                                                                </select>
                                                            </div>
                                                            <div style={{ flex: 1 }}>
                                                                {(memberValues?.ageType || 'dob') === 'dob' ? (
                                                                    <Controller
                                                                        control={control}
                                                                        name={`famille.${index}.datenaissanceFamille`}
                                                                        render={({ field }) => (
                                                                            <DatePicker
                                                                                selected={field.value ? new Date(field.value) : null}
                                                                                onChange={(date) => field.onChange(date ? date.toISOString() : '')}
                                                                                dateFormat="dd.MM.yyyy"
                                                                                showYearDropdown
                                                                                scrollableYearDropdown
                                                                                yearDropdownItemNumber={100}
                                                                                className="form-input"
                                                                                placeholderText="dd.mm.yyyy"
                                                                                locale={i18n.language.startsWith('fr') ? 'fr' : undefined}
                                                                                autoComplete='off'
                                                                                maxDate={new Date()}
                                                                            />
                                                                        )}
                                                                    />
                                                                ) : (
                                                                    <input
                                                                        type="number"
                                                                        className="form-input"
                                                                        placeholder="Age"
                                                                        {...register(`famille.${index}.age`)}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )

            case 2:
                return (
                    <div className="wizard-step">
                        <h3>{t('rendezvous.wizard.step3')}</h3>
                        <div className="summary-section">
                            <h4>{t('rendezvous.title')}</h4>
                            <div className="summary-grid">
                                <div>
                                    <span className="text-muted">{t('rendezvous.date')}</span>
                                    <strong>{formData.rdvDate ? new Date(formData.rdvDate).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</strong>
                                </div>
                            </div>
                        </div>
                        {/* ... Summary Content ... reuse */}
                        <div className="form-group mt-4">
                            <label className="form-label">{t('rendezvous.comment')}</label>
                            <textarea className="form-textarea" rows={4} {...register('commentaireAgent_rdv')} />
                        </div>
                    </div>
                )

            default: return null
        }
    }

    if (loading) return <div className="d-flex justify-content-center p-5"><Loader2 className="spin" size={32} /></div>

    return (
        <div className="rdv-add-page">
            <div className="page-header">
                <button className="btn btn-ghost" onClick={() => navigate('/rendezvous')}>
                    <ArrowLeft size={18} /> {t('common.back')}
                </button>
                <h1 className="page-title">{t('rendezvous.edit')} #{rdv?.numeroSequentiel}</h1>
            </div>

            <div className="card wizard-card">
                <div className="wizard-steps">
                    {steps.map((step, index) => (
                        <div key={step.key} className={`step-item ${currentStep === index ? 'active' : ''} ${currentStep > index ? 'completed' : ''}`}>
                            <div className="step-icon">
                                {currentStep > index ? <Check size={16} /> : <step.icon size={16} />}
                            </div>
                            <span>{t(`rendezvous.wizard.${step.key}`)}</span>
                        </div>
                    ))}
                </div>

                <div className="wizard-content">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        {renderStep()}

                        <div className="wizard-footer">
                            <button type="button" className="btn btn-secondary" onClick={previousStep} disabled={currentStep === 0}>
                                <ArrowLeft size={18} /> {t('common.previous')}
                            </button>

                            {currentStep < steps.length - 1 ? (
                                <button type="button" className="btn btn-primary" onClick={nextStep}>
                                    {t('common.next')} <ArrowRight size={18} />
                                </button>
                            ) : (
                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                    {submitting ? <Loader2 size={18} className="spin" /> : t('common.save')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}
